// Helper function to select natural English voice
export const selectNaturalVoice = () => {
  const voices = window.speechSynthesis.getVoices();

  return (
    voices.find(
      (voice) =>
        (voice.name.includes("Google US English") ||
          voice.name.includes("Microsoft David") ||
          voice.name.includes("Microsoft Zira") ||
          voice.name.includes("Samantha")) &&
        voice.lang === "en-US"
    ) ||
    voices.find(
      (voice) =>
        (voice.name.includes("Enhanced") || voice.name.includes("Premium")) &&
        voice.lang.startsWith("en-US")
    ) ||
    voices.find(
      (voice) =>
        voice.lang === "en-US" &&
        (voice.name.includes("Female") ||
          voice.name.includes("Zira") ||
          voice.name.includes("Samantha"))
    ) ||
    voices.find((voice) => voice.lang === "en-US") ||
    voices.find((voice) => voice.lang.startsWith("en"))
  );
};

// Helper function to format snake_case to Title Case
export const formatKey = (key) => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Helper function to extract parameters from fixture
export const extractFixtureParameters = (fixture) => {
  if (!fixture) return null;
  return fixture.request_payload || fixture.payload || null;
};

// Record the JSON type of each fixture value so edits can be coerced back to it.
// Text inputs hand back strings for everything, and providers do validate types:
// Experian answers 400 "lienSummary type must be true or false" for "true", and
// "DataPoints are required" for a stringified array.
export const captureParameterTypes = (params) => {
  const types = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    types[key] = Array.isArray(value) ? "array" : typeof value;
  });
  return types;
};

// Coerce a raw input string back to the type the fixture originally used.
// Anything unparseable is left as the raw string so the user still sees what
// they typed (and the provider's own validation message) rather than a silent
// rewrite of their input.
export const coerceParameterValue = (raw, type) => {
  if (typeof raw !== "string") return raw;

  if (type === "boolean") {
    const v = raw.trim().toLowerCase();
    if (v === "true") return true;
    if (v === "false") return false;
    return raw;
  }

  if (type === "number") {
    if (raw.trim() === "") return raw;
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n;
  }

  if (type === "array" || type === "object") {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }

  return raw;
};

// Generate request ID
export const generateRequestId = () => {
  return `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get method badge styles
export const getMethodBadgeClass = (method, isSelected) => {
  const baseClass = "text-xs font-bold px-2 py-0.5 rounded";

  if (method === "GET") {
    return `${baseClass} ${isSelected ? "bg-blue-500 text-white" : "bg-blue-100 text-blue-700"}`;
  } else if (method === "POST") {
    return `${baseClass} ${isSelected ? "bg-green-500 text-white" : "bg-green-100 text-green-700"}`;
  }
  return `${baseClass} ${isSelected ? "bg-orange-500 text-white" : "bg-orange-100 text-orange-700"}`;
};

// Get status color class
export const getStatusColorClass = (status) => {
  if (status >= 200 && status < 300) return "text-green-600";
  if (status >= 400) return "text-red-600";
  return "text-yellow-600";
};

/* ══════════════════════════════════════════════════════════════════════════
   Schema-driven field specs.

   The catalog endpoint already returns each endpoint's `request_schema`
   (types, `required`, `maxLength`, enums) — the panel used to throw all of it
   away and render every parameter as a bare text box. These helpers turn that
   schema into the information the UI needs to pick a control, mark required
   fields, and validate before spending a provider call.

   Everything here is provider-agnostic: it reads the schema, never a
   provider name.
   ══════════════════════════════════════════════════════════════════════════ */

// "body.taxId" -> { section: "body", name: "taxId" }; bare keys get no section.
export const splitParamKey = (key = "") => {
  const i = key.indexOf(".");
  if (i === -1) return { section: null, name: key };
  return { section: key.slice(0, i), name: key.slice(i + 1) };
};

// Tokens that should stay upper-cased in a human label.
const ACRONYMS = new Set([
  "id", "ids", "bin", "url", "uri", "ucc", "sic", "naics", "dbt", "ofac",
  "fsr", "zip", "pdf", "html", "json", "ssn", "ein", "api", "cage", "kyc",
  "bci", "vin", "fein", "usa", "us"
]);

// "taxId" -> "Tax ID"; "lienSummary" -> "Lien Summary"; "subcode" -> "Subcode".
export const humanizeName = (name = "") =>
  name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) =>
      ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(" ");

// Which control a value should get. Prefers the schema, falls back to the
// runtime type of the fixture value so endpoints without a schema still work.
const controlFor = (prop, value) => {
  const t = (prop?.type || "").toLowerCase();
  if (t === "boolean") return "boolean";
  if (t === "array") return "array";
  if (Array.isArray(prop?.enum) && prop.enum.length) return "enum";
  if (t === "integer" || t === "number") return "number";
  if (t === "string") return "text";
  if (typeof value === "boolean") return "boolean";
  if (Array.isArray(value)) return "array";
  if (typeof value === "number") return "number";
  return "text";
};

/**
 * Build a render spec per parameter, unioning the schema's properties with
 * whatever keys the fixture actually supplied (a fixture may omit optional
 * fields, and a schema may lag the fixture).
 */
export const buildFieldSpecs = (requestSchema, parameters = {}) => {
  const props = requestSchema?.properties || {};
  const required = new Set(requestSchema?.required || []);
  const keys = [...new Set([...Object.keys(props), ...Object.keys(parameters)])];

  return keys
    .filter((k) => {
      // Headers are transport plumbing, not request data the tester should edit.
      const { section } = splitParamKey(k);
      return section !== "header";
    })
    .map((key) => {
      const prop = props[key] || {};
      const { section, name } = splitParamKey(key);
      const value = parameters[key];
      const control = controlFor(prop, value);
      return {
        key,
        name,
        section,
        label: humanizeName(prop.name || name),
        control,
        required: required.has(key),
        maxLength: prop.maxLength || null,
        options: Array.isArray(prop.enum) ? prop.enum : null,
        // An array field with a documented default set is a multi-select: the
        // default enumerates the valid members (e.g. MultiSegments dataPoints).
        arrayOptions:
          control === "array"
            ? prop.items?.enum || prop.enum || null
            : null,
        describe: prop.description || null
      };
    })
    .sort((a, b) => {
      if (a.required !== b.required) return a.required ? -1 : 1;
      // Wide controls last so the two-column grid stays tidy.
      const wide = (s) => (s.control === "array" ? 1 : 0);
      if (wide(a) !== wide(b)) return wide(a) - wide(b);
      return a.label.localeCompare(b.label);
    });
};

/** Block the send when the provider would only answer 400. */
export const validateParameters = (specs, parameters = {}) => {
  const problems = [];
  specs.forEach((s) => {
    const v = parameters[s.key];
    const empty =
      v === undefined ||
      v === null ||
      v === "" ||
      (Array.isArray(v) && v.length === 0);

    if (s.required && empty) {
      problems.push({ key: s.key, label: s.label, message: "Required" });
      return;
    }
    if (!empty && s.maxLength && String(v).length > s.maxLength) {
      problems.push({
        key: s.key,
        label: s.label,
        message: `Must be ${s.maxLength} characters or fewer`
      });
    }
  });
  return problems;
};

/* ══════════════════════════════════════════════════════════════════════════
   Endpoint list: response format + grouping
   ══════════════════════════════════════════════════════════════════════════ */

// Providers expose the same report as JSON/HTML/PDF on sibling paths, which
// collapse to identical truncated labels in a narrow list. Surface the format
// as a badge so they stay distinguishable.
export const responseFormat = (endpoint) => {
  const ct = (
    endpoint?.response_schema?.content_type ||
    endpoint?.response_schema?.format ||
    ""
  ).toLowerCase();
  const path = (endpoint?.path || "").toLowerCase();

  if (path.endsWith("/pdf") || ct.includes("pdf")) return "PDF";
  if (path.endsWith("/html") || ct.includes("html")) return "HTML";
  if (path.endsWith(".zip") || ct.includes("zip")) return "ZIP";
  if (ct.includes("csv")) return "CSV";
  if (ct === "binary" || endpoint?.response_schema?.format === "binary")
    return "FILE";
  return "JSON";
};

export const formatBadgeClass = (fmt) =>
  ({
    JSON: "bg-sky-100 text-sky-700",
    HTML: "bg-amber-100 text-amber-800",
    PDF: "bg-red-100 text-red-700",
    ZIP: "bg-violet-100 text-violet-700",
    CSV: "bg-emerald-100 text-emerald-700",
    FILE: "bg-gray-200 text-gray-700"
  }[fmt] || "bg-gray-100 text-gray-600");

// Group by the first path segment after any version prefix, so report families
// and score families cluster. Groups of one collapse into "Other" rather than
// producing a wall of single-item headings.
export const groupEndpoints = (endpoints = []) => {
  const keyOf = (ep) => {
    const parts = (ep.path || "")
      .split("/")
      .filter(Boolean)
      .filter((p) => !/^v\d+$/i.test(p));
    return parts[0] || "other";
  };

  const buckets = new Map();
  endpoints.forEach((ep) => {
    const k = keyOf(ep);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(ep);
  });

  const groups = [];
  const other = [];
  [...buckets.entries()].forEach(([k, eps]) => {
    if (eps.length > 1) groups.push({ label: humanizeName(k), endpoints: eps });
    else other.push(eps[0]);
  });

  groups.sort((a, b) => b.endpoints.length - a.endpoints.length);
  if (other.length) {
    other.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    groups.push({ label: "Other", endpoints: other });
  }
  return groups;
};

export const filterEndpoints = (endpoints = [], q = "") => {
  const s = q.trim().toLowerCase();
  if (!s) return endpoints;
  return endpoints.filter(
    (ep) =>
      (ep.name || "").toLowerCase().includes(s) ||
      (ep.path || "").toLowerCase().includes(s) ||
      (ep.description || "").toLowerCase().includes(s)
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   Request preview
   ══════════════════════════════════════════════════════════════════════════ */

// Rebuild the request the way the backend's extract_request_parts does, so the
// preview and the cURL show what actually goes on the wire.
export const buildRequestPreview = (parameters = {}) => {
  const out = { path: {}, query: {}, header: {}, body: {} };
  Object.entries(parameters).forEach(([key, value]) => {
    if (value === "" || value === undefined || value === null) return;
    const { section, name } = splitParamKey(key);
    if (section === "path") out.path[name] = value;
    else if (section === "query") out.query[name] = value;
    else if (section === "header") out.header[name] = value;
    else out.body[section === "body" ? name : key] = value;
  });
  return out;
};

const shellQuote = (s) => `'${String(s).replace(/'/g, `'\\''`)}'`;

export const buildCurl = ({ baseUrl, endpoint, parameters }) => {
  const p = buildRequestPreview(parameters);
  let path = endpoint?.path || "";
  Object.entries(p.path).forEach(([k, v]) => {
    path = path.replace(`{${k}}`, encodeURIComponent(v));
  });

  const qs = new URLSearchParams(
    Object.entries(p.query).map(([k, v]) => [k, String(v)])
  ).toString();
  const url = `${(baseUrl || "").replace(/\/$/, "")}${path}${qs ? `?${qs}` : ""}`;

  const lines = [
    `curl -X ${endpoint?.method || "POST"} ${shellQuote(url)}`,
    `  -H ${shellQuote("Authorization: Bearer $TOKEN")}`
  ];
  Object.entries(p.header).forEach(([k, v]) =>
    lines.push(`  -H ${shellQuote(`${k}: ${v}`)}`)
  );
  if (Object.keys(p.body).length) {
    lines.push(`  -H ${shellQuote("Content-Type: application/json")}`);
    lines.push(`  -d ${shellQuote(JSON.stringify(p.body))}`);
  }
  return lines.join(" \\\n");
};

/* ══════════════════════════════════════════════════════════════════════════
   Response interpretation
   ══════════════════════════════════════════════════════════════════════════ */

// Providers return structured validation errors that are far more useful than
// the raw JSON they arrive in. Normalise the common shapes so the panel can
// list them and point at the offending field.
export const extractProviderErrors = (responseData) => {
  if (!responseData || typeof responseData !== "object") return [];
  const body = responseData.json && typeof responseData.json === "object"
    ? responseData.json
    : responseData;

  const raw = body.errors || body.error || body.Errors;
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];

  return list
    .map((e) => {
      if (typeof e === "string") return { message: e };
      const message = e.message || e.detail || e.description || e.msg;
      if (!message) return null;
      const code = e.errorCode ?? e.code ?? e.status ?? null;
      const type = e.errorType || e.type || null;
      // Most providers name the offending field at the start of the message.
      const m = /^([A-Za-z][A-Za-z0-9_]{2,})\b/.exec(String(message));
      return { code, type, message: String(message), fieldHint: m ? m[1] : null };
    })
    .filter(Boolean);
};

// Distinguish "succeeded, but the upstream had no records" from a failure —
// they look identical today and a thin response reads as a bug.
export const looksEmpty = (responseData) => {
  if (!responseData || typeof responseData !== "object") return false;
  const body = responseData.json && typeof responseData.json === "object"
    ? responseData.json
    : responseData;
  const results = body.results;
  if (results == null) return false;
  if (Array.isArray(results)) return results.length === 0;
  if (typeof results === "object") {
    const keys = Object.keys(results);
    // A lone header/identity segment means nothing substantive came back.
    return keys.length > 0 && keys.length <= 1;
  }
  return false;
};

// Match a provider's field hint back to one of our schema-keyed parameters.
export const matchSpecForField = (specs = [], fieldHint) => {
  if (!fieldHint) return null;
  const want = String(fieldHint).toLowerCase();
  return (
    specs.find((s) => s.name.toLowerCase() === want) ||
    specs.find((s) => s.name.toLowerCase().startsWith(want)) ||
    null
  );
};
